import { apiClient, ApiClient } from "@/lib/api/api-client";
import { Conversation } from "@/modules/types/conversation/conversation";
import {
  Attachment,
  Message,
  MessageCreate,
} from "@/modules/types/conversation/message";
import { BaseResponse } from "@/modules/types/response";

export interface ListConversationsResp extends BaseResponse {
  conversations: Conversation[];
  total: number;
}

export interface ListMessagesResp extends BaseResponse {
  messages: Message[];
  total: number;
  nextId?: string;
}

export interface MessageDataResponse extends BaseResponse {
  messageData: Message;
}

export interface ListAttachmentsResp extends BaseResponse {
  attachments: Attachment[];
  total: number;
  nextId?: string;
}

export interface UnreadConversationResp extends BaseResponse {
  total: number;
}

export class ConversationService {
  private BASE_PATH =
    process.env.API_CONVERSATION_ENDPOINT ||
    "http://localhost:8081/conversation";
  private apiClient: ApiClient = apiClient;
  private apiVersion = "v1.0.0";
  constructor(apiVersion?: string) {
    this.apiVersion = apiVersion || this.apiVersion;
  }

  async getConversations({
    businessId,
    jobId,
    candidateName,
    limit,
    offset,
  }: {
    businessId: string;
    jobId?: string;
    candidateName?: string;
    limit?: number;
    offset?: number;
  }): Promise<ListConversationsResp> {
    const params = new URLSearchParams();
    params.append("businessID", businessId);
    if (jobId && jobId.length > 0) params.append("jobID", jobId);
    if (candidateName && candidateName.length > 0)
      params.append("candidateName", candidateName);
    if (limit && limit > 0) params.append("limit", limit.toString());
    if (offset && offset > 0) params.append("offset", offset.toString());
    return this.apiClient.fetch<ListConversationsResp>(
      `${this.BASE_PATH}/${this.apiVersion}/list?${params.toString()}`,
      {
        method: "GET",
      }
    );
  }

  async getMessageByConversationId(
    conversationId: string,
    limit?: number,
    nextId?: string // fetch next page of messages
  ): Promise<ListMessagesResp> {
    const params = new URLSearchParams();
    if (limit && limit > 0) params.append("limit", limit.toString());
    if (nextId && nextId.length > 0) params.append("nextId", nextId);
    return this.apiClient.fetch<ListMessagesResp>(
      `${this.BASE_PATH}/${
        this.apiVersion
      }/${conversationId}/messages?${params.toString()}`,
      {
        method: "GET",
      }
    );
  }

  async sendMessage(message: MessageCreate): Promise<MessageDataResponse> {
    return this.apiClient.fetch<MessageDataResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/send-message`,
      {
        headers: {
          "content-type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(message),
      }
    );
  }

  async getAttachmentsOfConversation(
    conversationId: string,
    limit?: number,
    nextId?: string
  ): Promise<ListAttachmentsResp> {
    const params = new URLSearchParams();
    if (limit && limit > 0) params.append("limit", limit.toString());
    if (nextId && nextId.length > 0) params.append("nextId", nextId);
    return this.apiClient.fetch<ListAttachmentsResp>(
      `${this.BASE_PATH}/${
        this.apiVersion
      }/${conversationId}/attachments?${params.toString()}`,
      {
        method: "GET",
      }
    );
  }

  async markConversationAsRead(
    conversationId: string,
    businessId: string,
    businessFullName: string
  ): Promise<BaseResponse> {
    return this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/${conversationId}/mark-read`,
      {
        method: "POST",
        body: JSON.stringify({
          seenUserId: businessId,
          senderName: businessFullName,
        }),
      }
    );
  }

  async getTotalUnreadConversation(
    businessId: string
  ): Promise<UnreadConversationResp> {
    return this.apiClient.fetch<UnreadConversationResp>(
      `${this.BASE_PATH}/${this.apiVersion}/unread-total?businessId=${businessId}`,
      {
        method: "GET",
      }
    );
  }
}

export const conversationService = new ConversationService();
